import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUrlState, useUrlNumberState } from '@lib/useUrlState';
import { useUnitaAttive } from '@lib/unitaOrganizzativa';
import {
  Badge,
  Button,
  Combobox,
  type ComboboxOption,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@floraltech/ui';
import {
  AlertTriangle,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '@shell/AuthContext';
import { AiutoCampo } from '../components/AiutoCampo';
import { VUOTO, capitalizza, fmtDate, fmtEuro } from '../lib/formato';

/**
 * Elenco dei contratti a canone.
 *
 * Il filtro sta nella URL e non in uno stato locale: tornando indietro dal
 * dettaglio ci si ritrova la stessa schermata di prima, filtro compreso.
 *
 * Il menu degli stati si riempie dalla lookup configurabile del modulo, non
 * da una lista scritta qui: se l'amministratore aggiunge uno stato, compare
 * senza toccare il codice.
 *
 * Task 11 (referto dali, emendato 2026-08-02): filtro per direzione
 * (`direzione` nelle dipendenze di `carica`, altrimenti il filtro cambia la
 * URL e non ricarica - I9/c), colonna "Controparte" al posto di "Cliente"
 * (M1), colonna "Scade il" e colonna importo del canone al posto di
 * "Generazione" (M5, che su un passivo non significa niente), token
 * semantici di ui_core invece della tavolozza gray-* fissa (M4), stato con
 * la descrizione della lookup (M3).
 *
 * Review controllore 2026-08-03 (round 1): `location.state?.esito`
 * (l'annullamento dal dettaglio lo manda, stesso pattern della creazione)
 * mostrato una volta sola e ripulito con navigate(replace:true). Le righe
 * restano `role="row"` (un `role="button"` le toglierebbe dalla struttura
 * tabella per i lettori di schermo): il bersaglio da tastiera e' esplicito
 * sulla prima cella, l'onClick sulla riga resta solo una scorciatoia per il
 * mouse.
 *
 * Review controllore 2026-08-03 (round 2, e' la lista piu' usata):
 * `ricaricando` sostituisce `loading` a tutta pagina - il full-page scatta
 * SOLO al primo caricamento (`caricato === false`); Aggiorna e i filtri non
 * smontano piu' la tabella, solo disabilitano il pulsante (stesso pattern
 * del dettaglio). `caricaStati` e' richiamabile anche da Aggiorna: un errore
 * sulla lookup ha una via d'uscita senza F5.
 *
 * Task 8 BL-114 (referto dali sezione LISTA): GET /api/v1/contratti risponde
 * ora {contratti, totale} e la ricerca (`q`) e' passata al server insieme a
 * stato/direzione/unita_id - non c'e' piu' un filtro client su un elenco
 * gia' completo. Tre schede (Tabs) sostituiscono la Select "Direzione",
 * STESSA chiave URL `direzione`: i link esistenti restano validi. La
 * paginazione (`p`, `n` in URL) e il riepilogo "1-25 di N contratti" sono lo
 * stesso pattern di FattureList (documentale) - non si somma mai la pagina
 * caricata, il totale arriva dal server sull'insieme filtrato intero.
 *
 * "Prossimo canone" e "Fatturazione automatica" (referto dali) restano FUORI
 * da questa lista apposta: la risposta di elenco non porta quei dati e
 * calcolarli riga per riga significherebbe una fetch per contratto (N+1).
 * Servono campi in piu' nella risposta del backend - cantiere futuro, vedi
 * ledger.
 */

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/** Sentinella filtro ramo "tutti" (nessun unita_id in query). */
const TUTTI_I_RAMI = '';

const DIMENSIONE_PAGINA_DEFAULT = 25;
// Il backend accetta limit fino a 100 (le=100 su contratti.py `elenco`):
// non offrire scelte che verrebbero rifiutate.
const DIMENSIONI_PAGINA = [25, 50, 100];

interface Contratto {
  id: string;
  codice: string | null;
  tipo: string;
  stato: string;
  direzione: string;
  oggetto: string;
  ragione_sociale: string | null;
  unita_id: string | null;
  data_inizio: string;
  data_fine: string | null;
  periodicita: string | null;
  ricorrenza_attiva: boolean | null;
  ultima_generazione_at: string | null;
  importo_canone: string | null;
  canone_a_consumo: boolean;
}

/** Risposta paginata dell'elenco (Task 6 BL-114): `totale` e' il conteggio
 * sull'intero insieme filtrato, non sulla sola pagina caricata. */
interface ElencoContrattiResponse {
  contratti: Contratto[];
  totale: number;
}

interface VoceLookup {
  codice: string;
  descrizione: string;
}

const statoBadgeVariant = (
  stato: string,
): 'default' | 'secondary' | 'warning' | 'muted' => {
  const map: Record<string, 'default' | 'secondary' | 'warning' | 'muted'> = {
    attivo: 'default',
    bozza: 'secondary',
    sospeso: 'warning',
    cessato: 'muted',
  };
  return map[stato] ?? 'muted';
};

const ContrattiList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermesso } = useAuth();
  const puoGestire = hasPermesso('contratti.gestione');
  const { unita, loading: caricandoUnita } = useUnitaAttive();
  const [contratti, setContratti] = useState<Contratto[]>([]);
  const [totale, setTotale] = useState<number | null>(null);
  const [stati, setStati] = useState<VoceLookup[]>([]);
  const [erroreStati, setErroreStati] = useState<string | null>(null);
  // `ricaricando` disabilita il pulsante Aggiorna senza smontare la tabella;
  // il full-page "Caricamento..." scatta SOLO finche' `caricato` e' falso,
  // cioe' al primo tentativo (riuscito o no) - review controllore 2026-08-03
  // round 2, grave 1: e' la lista piu' usata, uno sbiancamento a ogni
  // Aggiorna o cambio filtro perde il punto a chi la sta scorrendo.
  const [ricaricando, setRicaricando] = useState(true);
  const [caricato, setCaricato] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // L'esito arriva una volta sola dalla history (creazione o annullamento):
  // il setter non serve mai dopo il valore iniziale, tenerlo in giro e' un
  // binding morto (review controllore round 2, minore 9).
  const [avviso] = useState<string | null>(
    (location.state as { esito?: string } | null)?.esito ?? null,
  );


  const [filtroStato, setFiltroStato] = useUrlState('stato', 'tutti');
  const [direzione, setDirezione] = useUrlState('direzione', 'tutti');
  const sezioneContratti = location.pathname.startsWith('/vendite/contratti')
    ? 'vendite'
    : location.pathname.startsWith('/acquisti/contratti')
      ? 'acquisti'
      : null;
  const baseLista = sezioneContratti === 'vendite'
    ? '/vendite/contratti'
    : sezioneContratti === 'acquisti'
      ? '/acquisti/contratti'
      : '/contratti';
  const [ricerca, setRicerca] = useUrlState('q', '');
  const [unitaId, setUnitaId] = useUrlState('unita', TUTTI_I_RAMI);
  useEffect(() => {
    if (location.search.includes('direzione=')) return;
    if (sezioneContratti === 'vendite' && direzione !== 'attivo') setDirezione('attivo');
    if (sezioneContratti === 'acquisti' && direzione !== 'passivo') setDirezione('passivo');
  }, [location.search, sezioneContratti, direzione, setDirezione]);
  // Testo digitato nel Combobox ramo: locale, non in URL (e' solo il filtro
  // di ricerca DENTRO la lista gia' caricata delle unita', stesso pattern di
  // LottiList - le unita' sono poche per costruzione, nessuna fetch server).
  const [ricercaRamo, setRicercaRamo] = useState('');

  // Paginazione in URL, stesso pattern di FattureList: tornando indietro da
  // un dettaglio si ritrova la stessa pagina, non la prima.
  const [pagina, setPagina] = useUrlNumberState('p', 1);
  const [perPagina, setPerPagina] = useUrlNumberState('n', DIMENSIONE_PAGINA_DEFAULT);

  // Ripulire subito lo state della history, altrimenti un F5 ripropone
  // l'esito.
  useEffect(() => {
    if ((location.state as { esito?: string } | null)?.esito) {
      navigate(location.pathname + location.search, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Firma dei filtri (paginazione esclusa): se cambia, si torna a pagina 1,
  // altrimenti si resterebbe su una pagina che nel nuovo insieme non esiste
  // (stesso pattern di FattureList).
  const firmaFiltri = [filtroStato, direzione, ricerca.trim(), unitaId].join('|');
  const firmaPrecedente = useRef(firmaFiltri);
  useEffect(() => {
    if (firmaPrecedente.current !== firmaFiltri) {
      firmaPrecedente.current = firmaFiltri;
      if (pagina !== 1) setPagina(1);
    }
  }, [firmaFiltri, pagina, setPagina]);

  const carica = useCallback(async () => {
    setRicaricando(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filtroStato !== 'tutti') params.set('stato', filtroStato);
      if (direzione !== 'tutti') params.set('direzione', direzione);
      if (unitaId !== TUTTI_I_RAMI) params.set('unita_id', unitaId);
      if (ricerca.trim()) params.set('q', ricerca.trim());
      params.set('limit', String(perPagina));
      params.set('offset', String(Math.max(0, (pagina - 1) * perPagina)));
      const res = await fetch(`/api/v1/contratti?${params}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Caricamento contratti non riuscito (${res.status})`);
      const data = (await res.json()) as ElencoContrattiResponse;
      setContratti(data.contratti);
      setTotale(data.totale);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore imprevisto');
    } finally {
      setRicaricando(false);
      setCaricato(true);
    }
    // `direzione`, `unitaId` e `ricerca` nelle dipendenze (referto dali c):
    // senza, cambiare il filtro sposta la URL ma non ricarica l'elenco.
  }, [filtroStato, direzione, unitaId, ricerca, pagina, perPagina]);

  useEffect(() => {
    void carica();
  }, [carica]);

  // Lookup configurabile: niente elenco di stati scritto nel codice. Un
  // fetch fallito (rete, permesso, 500) non deve restare un unhandled
  // rejection con la tendina muta: si dice cos'e' successo, distinto da "la
  // lookup e' vuota". Estratta (non piu' chiusa dentro il suo useEffect) per
  // poterla richiamare anche da Aggiorna: senza, un errore sulla lookup non
  // aveva via d'uscita senza un F5 (review controllore round 2, medio 5).
  const caricaStati = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/contratti/lookup/stato_contratto', {
        headers: authHeaders(),
      });
      if (!res.ok) {
        setErroreStati(`Elenco stati non disponibile (${res.status})`);
        return;
      }
      setStati(await res.json());
      setErroreStati(null);
    } catch {
      setErroreStati('Elenco stati non disponibile: errore di rete.');
    }
  }, []);

  useEffect(() => {
    void caricaStati();
  }, [caricaStati]);

  const aggiorna = () => {
    void carica();
    void caricaStati();
  };

  const descrizioneStato = (stato: string) =>
    stati.find((s) => s.codice === stato)?.descrizione ?? stato;

  // Risoluzione unita_id -> nome con UNA fetch (useUnitaAttive, gia' fatta al
  // mount): mai una fetch per riga.
  const nomeRamo = useCallback(
    (id: string | null): string => {
      if (!id) return VUOTO;
      return unita.find((u) => u.id === id)?.nome ?? VUOTO;
    },
    [unita],
  );

  // `contratti` e' gia' filtrato e paginato lato server (stato, direzione,
  // unita_id, q, limit, offset): la lunghezza di questo array e' quella
  // della PAGINA corrente, il conteggio sull'insieme intero e' `totale`.
  const filtriAttivi =
    filtroStato !== 'tutti' ||
    direzione !== 'tutti' ||
    ricerca.trim() !== '' ||
    unitaId !== TUTTI_I_RAMI;
  const azzeraFiltri = () => {
    setFiltroStato('tutti');
    setDirezione('tutti');
    setRicerca('');
    setUnitaId(TUTTI_I_RAMI);
    setRicercaRamo('');
  };

  const vaiAlDettaglio = (id: string) =>
    // Porta con se' la propria URL (filtri e pagina compresi): e' cio' che
    // permette al dettaglio di tornare qui CONSERVANDO i filtri dopo un
    // annullamento, senza affidarsi a navigate(-1) che si rompe se l'utente
    // e' arrivato da un'altra pagina o da un link diretto.
    navigate(`${baseLista}/${id}`, { state: { elencoPath: baseLista, elencoSearch: location.search } });

  // Opzioni Combobox ramo (filtro pagina): sentinella "Tutti i rami" +
  // ricerca client-side sulla lista gia' caricata (stesso pattern di
  // LottiList - le unita' sono poche per costruzione).
  const opzioniRamo: ComboboxOption[] = [
    { value: TUTTI_I_RAMI, label: 'Tutti i rami' },
    ...unita
      .filter((u) => {
        const q = ricercaRamo.trim().toLowerCase();
        if (!q) return true;
        return u.nome.toLowerCase().includes(q) || (u.codice ?? '').toLowerCase().includes(q);
      })
      .map<ComboboxOption>((u) => ({
        value: u.id,
        label: u.nome,
        sublabel: u.codice ?? u.tipo,
      })),
  ];
  const valoreComboboxRamo = (() => {
    // Il testo digitato ha sempre precedenza mentre si scrive: le etichette
    // sentinella si mostrano solo a ricerca vuota (stato quiescente che
    // riflette il filtro attivo in URL), altrimenti il testo digitato
    // sparirebbe al render successivo (campo non digitabile).
    if (ricercaRamo !== '') return ricercaRamo;
    if (unitaId === TUTTI_I_RAMI) return 'Tutti i rami';
    const sel = unita.find((u) => u.id === unitaId);
    return sel ? sel.nome : '';
  })();

  // Riepilogo "1-25 di N contratti" (Task 8, pattern FattureList): il totale
  // NON si calcola sommando le righe caricate, arriva dal server
  // sull'intero insieme filtrato.
  const primoDellaPagina = !totale ? 0 : (pagina - 1) * perPagina + 1;
  const ultimoDellaPagina = (pagina - 1) * perPagina + contratti.length;
  const ultimaPagina = totale === null ? pagina : Math.max(1, Math.ceil(totale / perPagina));

  // Etichetta di una scheda: il conteggio compare SOLO su quella attiva, dal
  // `totale` server dell'insieme filtrato corrente - le altre due schede non
  // hanno un conteggio proprio senza una fetch in piu' per scheda.
  const etichettaScheda = (valore: string, label: string) =>
    direzione === valore && caricato && totale !== null ? `${label} (${totale})` : label;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <CalendarClock className="w-6 h-6" />
          Contratti a canone
        </h1>
        <div className="flex items-center gap-2">
          {puoGestire && (
            <>
              {/* Task 7 BL-114: due porte d'ingresso, la scelta "Chi paga?"
                  arriva gia' decisa in URL (referto dali RACCOMANDAZIONE). */}
              <Button variant="outline" onClick={() => navigate('/vendite/contratti/nuovo?tipo_rapporto=cliente')}>
                <Plus className="w-4 h-4" />
                Nuovo contratto a un cliente
              </Button>
              <Button onClick={() => navigate('/acquisti/contratti/nuovo?tipo_rapporto=fornitore')}>
                <Plus className="w-4 h-4" />
                Nuova spesa ricorrente
              </Button>
            </>
          )}
          <Button variant="outline" onClick={aggiorna} disabled={ricaricando}>
            <RefreshCw className="w-4 h-4" />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Tre schede: STESSA chiave URL 'direzione' della vecchia Select, i
          link esistenti verso ?direzione=attivo/passivo restano validi. */}
      <Tabs value={direzione} onValueChange={setDirezione}>
        <TabsList>
          <TabsTrigger value="attivo">{etichettaScheda('attivo', 'Da incassare')}</TabsTrigger>
          <TabsTrigger value="passivo">{etichettaScheda('passivo', 'Da pagare')}</TabsTrigger>
          <TabsTrigger value="tutti">{etichettaScheda('tutti', 'Tutti')}</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap items-start gap-3">
        <Input
          type="search"
          value={ricerca}
          onChange={(e) => setRicerca(e.target.value)}
          placeholder="Cerca per codice, controparte o oggetto"
          className="flex-1 min-w-[240px]"
        />
        <div className="w-56">
          <Combobox
            value={valoreComboboxRamo}
            onInputChange={(v) => {
              setRicercaRamo(v);
              if (unitaId !== TUTTI_I_RAMI) setUnitaId(TUTTI_I_RAMI);
            }}
            onSelect={(opt) => {
              setRicercaRamo('');
              setUnitaId(opt.value);
            }}
            options={opzioniRamo}
            placeholder="Cerca ramo..."
            loading={caricandoUnita}
            ariaLabel="Filtra per ramo"
          />
        </div>
        <div>
          <Select value={filtroStato} onValueChange={setFiltroStato}>
            <SelectTrigger className="w-48" aria-label="Filtra per stato">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti gli stati</SelectItem>
              {stati.map((s) => (
                <SelectItem key={s.codice} value={s.codice}>
                  {s.descrizione}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {erroreStati && (
            <p role="alert" className="mt-1 text-xs text-destructive">
              {erroreStati}
            </p>
          )}
        </div>
      </div>

      {error && (
        <div role="alert" className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}
      {avviso && (
        <div className="p-3 rounded-md bg-primary/10 text-primary text-sm">{avviso}</div>
      )}

      {caricato && !error && (
        <p className="text-sm text-muted-foreground">
          {totale === null
            ? `${contratti.length} risultati in questa pagina`
            : totale === 0
              ? 'Nessun risultato'
              : `${primoDellaPagina}-${ultimoDellaPagina} di ${totale} contratti`}
        </p>
      )}

      {!caricato ? (
        <p className="text-sm text-muted-foreground">Caricamento...</p>
      ) : (totale ?? contratti.length) === 0 ? (
        filtriAttivi ? (
          // Un filtro (stato/direzione/ramo/ricerca, tutti server-side) e'
          // acceso e non trova nulla: creare un contratto non risolve questo
          // caso, azzerare il filtro si'.
          <div className="text-sm text-muted-foreground space-y-2">
            <p>Nessun contratto corrisponde ai filtri applicati.</p>
            <Button variant="outline" onClick={azzeraFiltri}>
              Azzera i filtri
            </Button>
          </div>
        ) : (
          // Nessun filtro attivo e zero risultati: il tenant non ha ancora
          // nessun contratto. L'invito a crearne uno vive dietro il gate del
          // permesso, come il pulsante che lo fa davvero.
          <div className="text-sm text-muted-foreground space-y-2">
            <p>Nessun contratto a canone.{puoGestire ? ' Creane uno con il pulsante qui sopra.' : ''}</p>
            {puoGestire && (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => navigate('/vendite/contratti/nuovo?tipo_rapporto=cliente')}>
                  <Plus className="w-4 h-4" />
                  Nuovo contratto a un cliente
                </Button>
                <Button onClick={() => navigate('/acquisti/contratti/nuovo?tipo_rapporto=fornitore')}>
                  <Plus className="w-4 h-4" />
                  Nuova spesa ricorrente
                </Button>
              </div>
            )}
          </div>
        )
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Codice</TableHead>
              <TableHead>Controparte</TableHead>
              <TableHead>Oggetto</TableHead>
              {/* Direzione: via quando una scheda specifica e' attiva -
                  ridondante, la scheda gia' lo dice (referto dali). */}
              {direzione === 'tutti' && <TableHead>Direzione</TableHead>}
              <TableHead>Ramo</TableHead>
              <TableHead>Periodicita</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead>Decorrenza</TableHead>
              <TableHead>
                <span className="inline-flex items-center gap-1">
                  Scade il
                  <AiutoCampo
                    campo="scade il"
                    testo="Data di fine contratto (data_fine), se prevista. Un contratto senza questa data non ha una scadenza da rinnovare o disdire."
                  />
                </span>
              </TableHead>
              <TableHead className="text-right">
                <span className="inline-flex items-center gap-1 justify-end">
                  Canone/periodo
                  <AiutoCampo
                    campo="canone per periodo"
                    testo="Totale di un periodo (IVA inclusa), calcolato dalle voci attive di oggi. 'A consumo' quando una voce ha una quantita' rilevata a posteriori: non c'e' una cifra fissa da mostrare."
                  />
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contratti.map((c) => (
              <TableRow key={c.id} onClick={() => vaiAlDettaglio(c.id)} className="cursor-pointer">
                <TableCell className="font-mono text-xs">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      vaiAlDettaglio(c.id);
                    }}
                    aria-label={`Apri il contratto ${c.oggetto}`}
                    className="-ml-1 rounded-sm px-1 py-1 text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                  >
                    {c.codice ?? VUOTO}
                  </button>
                </TableCell>
                <TableCell>{c.ragione_sociale ?? VUOTO}</TableCell>
                <TableCell className="max-w-xs truncate" title={c.oggetto}>
                  {c.oggetto}
                </TableCell>
                {direzione === 'tutti' && (
                  <TableCell>
                    <Badge variant={c.direzione === 'passivo' ? 'secondary' : 'default'}>
                      {c.direzione === 'passivo' ? 'Spesa' : 'Incasso'}
                    </Badge>
                  </TableCell>
                )}
                <TableCell>{nomeRamo(c.unita_id)}</TableCell>
                <TableCell>{c.periodicita ? capitalizza(c.periodicita) : VUOTO}</TableCell>
                <TableCell>
                  <Badge variant={statoBadgeVariant(c.stato)}>{descrizioneStato(c.stato)}</Badge>
                </TableCell>
                <TableCell className="whitespace-nowrap">{fmtDate(c.data_inizio)}</TableCell>
                <TableCell className="whitespace-nowrap">{fmtDate(c.data_fine)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {c.importo_canone != null
                    ? fmtEuro(c.importo_canone)
                    : c.canone_a_consumo
                      ? 'a consumo'
                      : VUOTO}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Paginazione: senza, i contratti oltre la prima pagina erano
          invisibili (Task 8, stesso pattern di FattureList). */}
      {caricato && !error && (totale === null || totale > 0) && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <span>Righe per pagina</span>
            <Select
              value={String(perPagina)}
              onValueChange={(v) => {
                setPerPagina(Number(v));
                setPagina(1);
              }}
            >
              <SelectTrigger className="h-8 w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIMENSIONI_PAGINA.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={ricaricando || pagina <= 1}
              onClick={() => setPagina(Math.max(1, pagina - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
              Precedente
            </Button>
            <span className="px-1 text-muted-foreground">
              Pagina {pagina}
              {totale !== null && ` di ${ultimaPagina}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={
                ricaricando ||
                (totale !== null ? pagina >= ultimaPagina : contratti.length < perPagina)
              }
              onClick={() => setPagina(pagina + 1)}
            >
              Successiva
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContrattiList;
